import { useMemo } from 'react';

import {
    CommonAreaReadModel,
    ParkingSpotReadModel,
    RoomReadModel,
    SpaceDiscriminator,
    SpaceReadModel,
    useGetCommonAreasByIdQuery,
    useGetParkingSpotsByIdQuery,
    useGetRoomsByIdQuery,
    useGetSpacesByIdQuery
} from '@client/api';

type SpaceData = SpaceReadModel | RoomReadModel | ParkingSpotReadModel | undefined;

export const useGetGenericSpace = (spaceId: string | undefined | null, discriminator: SpaceDiscriminator | undefined | null) => {

  const isCommonAreaDiscriminator = useMemo(() => discriminator === 'CommonArea', [discriminator]);
  const isRoomDiscriminator = useMemo(() => discriminator === 'Room', [discriminator]);
  const isParkingSpotDiscriminator = useMemo(() => discriminator === 'ParkingSpot', [discriminator]);
  const isSpaceDiscriminator = useMemo(() => !isCommonAreaDiscriminator && !isRoomDiscriminator && !isParkingSpotDiscriminator, [isCommonAreaDiscriminator, isParkingSpotDiscriminator, isRoomDiscriminator]);

  const spaceResult = useGetSpacesByIdQuery({
    id: spaceId ?? '',
  }, { skip: spaceId == null || !isSpaceDiscriminator });

  const commonAreaResult = useGetCommonAreasByIdQuery({
    id: spaceId ?? '',
  }, { skip: spaceId == null || !isCommonAreaDiscriminator });

  const roomResult = useGetRoomsByIdQuery({
    id: spaceId ?? '',
  }, { skip: spaceId == null || !isRoomDiscriminator });

  const parkingSpotResult = useGetParkingSpotsByIdQuery({
    id: spaceId ?? '',
  }, { skip: spaceId == null || !isParkingSpotDiscriminator });

  let result = spaceResult
  let data = spaceResult.data;

  if (isCommonAreaDiscriminator) {
    result = commonAreaResult;
    data = commonAreaResult.data;
  } else if (isRoomDiscriminator) {
    result = roomResult;
    data = roomResult.data;
  } else if (isParkingSpotDiscriminator) {
    result = parkingSpotResult;
    data = parkingSpotResult.data;
  }

  const isCommonArea = (data: SpaceData): data is CommonAreaReadModel => !!(data && data.discriminator === 'CommonArea' && 'type' in data);
  const isRoom = (data: SpaceData): data is RoomReadModel => !!(data && data.discriminator === 'Room' && 'type' in data);
  const isParkingSpot = (data: SpaceData): data is ParkingSpotReadModel => !!(data && data.discriminator === 'ParkingSpot' && 'type' in data);
  const isSpace = (data: SpaceData): data is SpaceReadModel => !isRoom(data) && !isParkingSpot(data);

  return { ...result,
    data: data as SpaceData,
    isCommonArea,
    isRoom,
    isParkingSpot,
    isSpace
  }
}
